import { useCallback, useEffect, useRef, useState } from 'react'
import apiClient from '../services/apiClient'
import { getOrCreateDeviceId } from '../utils/deviceId'
import { usePlaybackLease } from './usePlaybackLease'

/** Refresh before the default 30m playback JWT expires so Range seeks keep working. */
const TOKEN_REFRESH_MS = 20 * 60 * 1000
const MAX_STREAM_RECOVERIES = 2

export type PlaybackStreamType = 'recording' | 'video'
export type PlaybackFormat = 'hls' | 'mp4'

type PlaybackApiError = {
  response?: {
    status?: number
    data?: { message?: string; code?: string }
  }
}

/** Map API failures to learner-safe copy — never scare users with lock/device when video is simply missing. */
export function resolvePlaybackTokenError(err: unknown): string {
  const ax = err as PlaybackApiError
  const status = ax.response?.status
  const code = ax.response?.data?.code
  const apiMessage = ax.response?.data?.message?.trim()

  if (code === 'ACCOUNT_LOCKED') {
    return (
      apiMessage ||
      'Tài khoản đã bị khóa do nghi ngờ chia sẻ. Vui lòng liên hệ quản trị viên để mở khóa.'
    )
  }
  if (code === 'CONCURRENT_STREAM' || code === 'DEVICE_LIMIT') {
    return apiMessage || 'Không thể phát video từ thiết bị này. Vui lòng đóng phiên xem trên thiết bị khác.'
  }
  if (status === 404 || code === 'MEDIA_NOT_READY') {
    return apiMessage || 'Video chưa sẵn sàng. Vui lòng quay lại sau khi buổi học kết thúc.'
  }
  if (status === 403 && apiMessage) {
    return apiMessage
  }
  if (apiMessage && apiMessage.toLowerCase() !== 'forbidden') {
    return apiMessage
  }
  return 'Không thể phát video. Vui lòng tải lại trang hoặc thử lại sau.'
}

export function resolvePlaybackFormat(value: unknown): PlaybackFormat {
  return value === 'hls' ? 'hls' : 'mp4'
}

export function buildPlaybackStreamUrl(
  baseUrl: string,
  token: string,
  format: PlaybackFormat = 'mp4'
): string {
  const base = baseUrl.replace(/\/$/, '')
  if (format === 'hls') {
    return `${base}/student/playback/hls/${token}/index.m3u8`
  }
  return `${base}/student/playback/stream/${token}`
}

interface ResumeState {
  time: number
  wasPlaying: boolean
  rate: number
}

interface UseSecurePlaybackStreamOptions {
  /** When false, skip fetch/refresh and release the lease (e.g. user pressed Stop). */
  active?: boolean
}

/**
 * Issues a short-lived playback stream URL and keeps it fresh for long watches.
 * Heartbeat renews the device lease; this hook renews the JWT embedded in `<video src>`.
 */
export function useSecurePlaybackStream(
  type: PlaybackStreamType,
  id: number,
  options: UseSecurePlaybackStreamOptions = {}
) {
  const active = options.active !== false
  const [playback, setPlayback] = useState<{
    token: string
    resourceId: number
    leaseToken?: string
    format: PlaybackFormat
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const resumeRef = useRef<ResumeState | null>(null)
  const requestSeq = useRef(0)
  const recoveriesRef = useRef(0)

  usePlaybackLease(
    Boolean(playback) && active,
    playback
      ? { type, id: playback.resourceId, leaseToken: playback.leaseToken }
      : undefined
  )

  const loadToken = useCallback(
    async (opts?: { preservePosition?: boolean }) => {
      if (!id || id <= 0) return

      const video = videoRef.current
      if (opts?.preservePosition && video && Number.isFinite(video.currentTime)) {
        resumeRef.current = {
          time: video.currentTime,
          wasPlaying: !video.paused,
          rate: video.playbackRate || 1,
        }
      }

      const seq = ++requestSeq.current
      try {
        setError(null)
        const response = await apiClient.get('/student/playback/token', {
          params: { type, id, deviceId: getOrCreateDeviceId() },
        })
        if (seq !== requestSeq.current) return
        if (response.data.success) {
          setPlayback({
            token: response.data.data.playbackToken,
            leaseToken: response.data.data.leaseToken,
            resourceId: id,
            format: resolvePlaybackFormat(response.data.data.format),
          })
          // Successful token issue resets stream-error recovery budget.
          if (!opts?.preservePosition) {
            recoveriesRef.current = 0
          }
        }
      } catch (err: unknown) {
        console.error('Failed to load playback token', err)
        if (seq !== requestSeq.current) return
        setPlayback(null)
        setError(resolvePlaybackTokenError(err))
      }
    },
    [type, id]
  )

  useEffect(() => {
    if (!active) {
      return
    }

    setPlayback(null)
    recoveriesRef.current = 0
    void loadToken()

    const timer = window.setInterval(() => {
      void loadToken({ preservePosition: true })
    }, TOKEN_REFRESH_MS)

    return () => {
      requestSeq.current += 1
      window.clearInterval(timer)
    }
  }, [type, id, active, loadToken])

  const format: PlaybackFormat = playback?.format ?? 'mp4'
  const streamUrl = playback
    ? buildPlaybackStreamUrl(apiClient.defaults.baseURL || '/api/v1', playback.token, format)
    : null

  const applyResumeOnMetadata = useCallback((video: HTMLVideoElement, fallbackRate?: number) => {
    // Stream URL accepted media → clear prior 403/network recovery budget.
    recoveriesRef.current = 0
    const resume = resumeRef.current
    if (resume) {
      video.playbackRate = fallbackRate ?? resume.rate
      if (resume.time > 0) {
        try {
          video.currentTime = resume.time
        } catch {
          /* ignore seek until more data */
        }
      }
      if (resume.wasPlaying) {
        void video.play().catch(() => {
          /* autoplay may be blocked after src swap */
        })
      }
      resumeRef.current = null
      return
    }
    if (typeof fallbackRate === 'number') {
      video.playbackRate = fallbackRate
    }
  }, [])

  const recoverFromStreamError = useCallback(() => {
    if (recoveriesRef.current >= MAX_STREAM_RECOVERIES) {
      setError('Không thể phát video. Vui lòng tải lại trang hoặc thử lại sau.')
      return
    }
    recoveriesRef.current += 1
    void loadToken({ preservePosition: true })
  }, [loadToken])

  return {
    streamUrl,
    format,
    error,
    videoRef,
    reload: loadToken,
    applyResumeOnMetadata,
    recoverFromStreamError,
  }
}
